import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["clearable", "extraTimes", "format", "interval", "max", "maxValue", "min", "minValue", "value", "menuWidth", "size", "use", "placeholder", "interval"];
import unescapedText from 'I18n/utils/unescapedText';
import I18n from 'I18n';
import devLogger from 'react-utils/devLogger';
import { TimeIcon } from 'foundations-assets';
import { Controllable } from '../Controllable';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { Select } from '../Select';
import { TypeaheadResultsItem } from '../Typeahead/TypeaheadResultsItem';
import { forwardRef, useMemo } from 'react';
import { virtualizedMenuRenderer } from '../../utils/virtualizedMenuRenderer';
import { jsx as _jsx } from "react/jsx-runtime";
const MINUTES_IN_A_DAY = 24 * 60;
const NUMBER_REGEX = /\d/;
const condense = str => str.replace(/\s/g, '').toLowerCase();
const formatTime = (format, value) => I18n.moment.utc(0).minutes(value).format(format);
const generateOptions = (extraTimes, format, interval, max, min, normalizedValue) => {
  if (interval <= 0) {
    const msg = 'TimePicker: Interval cannot be a number less than or equal to 0';
    devLogger.warn({
      message: msg,
      key: msg
    });
  }
  const calculatedInterval = interval <= 0 ? 1 : interval;
  const options = [];
  const computedMin = typeof min === 'number' ? min : 0;
  const computedMax = typeof max === 'number' ? max : MINUTES_IN_A_DAY;
  for (let value = 0; value < MINUTES_IN_A_DAY; value += calculatedInterval) {
    const outOfRange = computedMin > value || value > computedMax;
    if (outOfRange) {
      continue;
    }
    const text = formatTime(format, value);
    options.push({
      text,
      value
    });
    if (normalizedValue && normalizedValue > value && normalizedValue < value + calculatedInterval) {
      const normalizedValueText = I18n.moment.utc(0).minutes(normalizedValue).format(format);
      options.push({
        text: normalizedValueText,
        value: normalizedValue
      });
    }
  }
  extraTimes.forEach(value => {
    if (!options.some(option => option.value === value)) {
      const text = formatTime(format, value);
      options.push({
        text,
        value
      });
    }
  });
  return options;
};
const normalizeValue = (format, value) => {
  // Return a string corresponding to the number of minutes into the day
  // represented by the value.
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  devLogger.warn({
    message: `TimePicker: String value "${value}" given. This can cause inconsistent behavior.`,
    key: 'TimePicker: string value'
  });
  const moment = I18n.moment.utc(value, format);
  return moment.hours() * 60 + moment.minutes();
};
const optionMatchesSearch = (option, search) => {
  var _option$text$match;
  if (!search) {
    return true;
  }
  // Ignore whitespace and casing
  const condensedSearch = condense(search);
  const condensedOptionText = condense(option.text);

  // Match front-to-back, allowing chars to be skipped (except the first
  // number). So e.g. a search for "8" matches all times of the form "8:xx",
  // and a search for "815" matches "8:15 AM" and "8:15 PM".
  const searchRegexMatch = condensedSearch.match(NUMBER_REGEX);
  if (searchRegexMatch && searchRegexMatch[0] !== ((_option$text$match = option.text.match(NUMBER_REGEX)) === null || _option$text$match === void 0 ? void 0 : _option$text$match[0])) {
    return false;
  }
  let lastSearchCharMatchPos = -1;
  for (let i = 0; i < condensedSearch.length; i++) {
    const searchCharMatchPos = condensedOptionText.indexOf(condensedSearch[i], lastSearchCharMatchPos + 1);
    if (searchCharMatchPos === -1) {
      return false;
    }
    lastSearchCharMatchPos = searchCharMatchPos;
  }
  return true;
};
const getSearchResults = (options, search) => {
  const filteredOptions = options.filter(option => optionMatchesSearch(option, search));
  const exactMatches = [];
  const otherMatches = [];
  filteredOptions.forEach(option => {
    if (condense(option.text).startsWith(condense(search))) {
      exactMatches.push(option);
    } else {
      otherMatches.push(option);
    }
  });
  return [...exactMatches, ...otherMatches];
};
const TimePickerIcon = ({
  buttonUse,
  buttonSize
}) => {
  return /*#__PURE__*/_jsx(TimeIcon, {
    size: buttonUse === 'link' ? '200' : buttonSize === 'sm' ? '100' : '300',
    color: buttonUse === 'link' ? 'icon-core-subtle' : undefined
  });
};
const valueRenderer = (option, buttonUse, buttonSize) => /*#__PURE__*/_jsx(TypeaheadResultsItem, {
  "aria-invalid": option.invalid,
  itemComponent: "span",
  option: Object.assign({}, option, {
    icon: () => /*#__PURE__*/_jsx(TimePickerIcon, {
      buttonUse: buttonUse,
      buttonSize: buttonSize
    })
  }),
  _useButton: false,
  _useDropdownText: false
});
const getButtonSize = (shorthandSize, use) => {
  if (shorthandSize === 'sm' && use === 'on-dark') {
    return 'sm';
  }
  return 'default';
};
const getButtonUse = (shorthandSize, use) => {
  if (use === 'on-dark') {
    return shorthandSize === 'sm' ? 'tertiary-extra-light' : 'form-on-dark';
  }
  return shorthandSize === 'sm' ? 'link' : 'form';
};
const getMenuWidth = (menuWidth, shorthandSize) => {
  if (menuWidth) {
    return menuWidth;
  }
  if (shorthandSize === 'sm') {
    return 300;
  }
  return undefined;
};
export const TimePickerComponent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      clearable = true,
      extraTimes = [],
      format = 'LT',
      interval = 30,
      max,
      maxValue,
      min,
      minValue,
      value,
      menuWidth,
      size = 'default',
      use = 'default',
      placeholder = unescapedText('salesUI.UITimePicker.placeholder')
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Replicate getDerivedStateFromProps logic with useMemo
  const options = useMemo(() => {
    const normalizedValue = normalizeValue(format, value !== null && value !== void 0 ? value : null);
    const generatedOptions = generateOptions(extraTimes, format, interval, max || maxValue, min || minValue, normalizedValue);
    return generatedOptions;
  }, [extraTimes, format, interval, max, maxValue, min, minValue, value]);
  const shorthandSize = toShorthandSize(size);
  const buttonSize = getButtonSize(shorthandSize, use);
  const buttonUse = getButtonUse(shorthandSize, use);
  const computedMenuWidth = getMenuWidth(menuWidth, shorthandSize);
  return /*#__PURE__*/_jsx(Select, Object.assign({}, rest, {
    buttonSize: buttonSize,
    buttonUse: buttonUse,
    clearable: clearable,
    filterOptions: getSearchResults,
    menuWidth: computedMenuWidth,
    menuRenderer: virtualizedMenuRenderer,
    options: options,
    placeholder: placeholder,
    ref: ref,
    resetValue: null,
    value: normalizeValue(format, value !== null && value !== void 0 ? value : null),
    valueRenderer: option => valueRenderer(option, buttonUse, buttonSize)
  }));
});
export const TimePicker = Controllable(TimePickerComponent, ['value']);